import api from './apiClient';
import { getAnonymousVisitorKey } from '../utils/anonymousVisitorKey';

// Choice types enum
export const ChoiceType = {
  Normal: 0,
  Range: 1,
  FreeText: 2,
};

export const ChoiceTypeLabels = {
  [ChoiceType.Normal]: 'Normal (Text/Image)',
  [ChoiceType.Range]: 'Range (Numeric Slider)',
  [ChoiceType.FreeText]: 'Free Text',
};

/**
 * Helper to build FormData from an object, handling File objects and nested values.
 * @param {Object} data - Data object with fields to include
 * @param {File|null} image - Optional image file
 * @returns {FormData}
 */
const buildFormData = (data, image = null) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'boolean') {
      formData.append(key, value.toString());
    } else if (typeof value === 'number') {
      formData.append(key, value.toString());
    } else {
      formData.append(key, value);
    }
  });

  if (image instanceof File) {
    formData.append('image', image);
  }

  return formData;
};

// ===================
// Survey CRUD
// ===================

export const getSurveys = async (params = {}, options = {}) => {
  return api.get('/surveys', params, options);
};

/**
 * Admin-only: Get all surveys in the system
 * Used for assigning registration surveys to countries
 * @param {Object} params - Query params (page, pageSize, search, isPublished)
 * @param {Object} options - Request options
 */
export const getAllSurveys = async (params = {}, options = {}) => {
  return api.get('/surveys/admin/all', params, options);
};

/**
 * Get the published survey library for cross-feature assignments.
 * @param {string} search - Optional search term
 * @param {Object} options - Request options
 * @returns {Promise<Array>}
 */
export const getPublishedSurveyLibrary = async (search = '', options = {}) => {
  return api.get('/surveys/published-library', { search }, options);
};

export const getSurvey = async (id, includeDeleted = false, options = {}) => {
  return api.get(`/surveys/${id}`, { includeDeleted }, options);
};

/**
 * Create a new survey with optional image
 * @param {Object} data - Survey data (name, introText, finishText, allowAnonymous)
 * @param {File|null} image - Optional survey image
 * @param {Object} options - Request options
 */
export const createSurvey = async (data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.upload('/surveys', formData, options);
};

/**
 * Update a survey with optional image
 * @param {number} id - Survey ID
 * @param {Object} data - Survey data (name, introText, finishText, allowAnonymous, clearImage)
 * @param {File|null} image - Optional new survey image
 * @param {Object} options - Request options
 */
export const updateSurvey = async (id, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.uploadPut(`/surveys/${id}`, formData, options);
};

export const deleteSurvey = async (id, options = {}) => {
  return api.delete(`/surveys/${id}`, options);
};

export const publishSurvey = async (id, options = {}) => {
  return api.post(`/surveys/${id}/publish`, {}, options);
};

export const unpublishSurvey = async (id, options = {}) => {
  return api.post(`/surveys/${id}/unpublish`, {}, options);
};

// ===================
// Question CRUD
// ===================

/**
 * Add a question to a survey with optional image
 * @param {number} surveyId - Survey ID
 * @param {Object} data - Question data (body, isRequired)
 * @param {File|null} image - Optional question image
 * @param {Object} options - Request options
 */
export const addQuestion = async (surveyId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.upload(`/surveys/${surveyId}/questions`, formData, options);
};

/**
 * Update a question with optional image
 * @param {number} questionId - Question ID
 * @param {Object} data - Question data (body, isRequired, clearImage)
 * @param {File|null} image - Optional new question image
 * @param {Object} options - Request options
 */
export const updateQuestion = async (questionId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.uploadPut(`/surveys/questions/${questionId}`, formData, options);
};

export const deleteQuestion = async (questionId, options = {}) => {
  return api.delete(`/surveys/questions/${questionId}`, options);
};

export const reorderQuestions = async (surveyId, questionIds, options = {}) => {
  return api.post(`/surveys/${surveyId}/questions/reorder`, { questionIds }, options);
};

// ===================
// Choice CRUD
// ===================

/**
 * Add a choice to a question with optional image (for Normal type only)
 * @param {number} questionId - Question ID
 * @param {Object} data - Choice data (choiceType, text, rangeMin, rangeMax, etc.)
 * @param {File|null} image - Optional choice image (only for Normal type)
 * @param {Object} options - Request options
 */
export const addChoice = async (questionId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.upload(`/surveys/questions/${questionId}/choices`, formData, options);
};

/**
 * Update a choice with optional image (for Normal type only)
 * @param {number} choiceId - Choice ID
 * @param {Object} data - Choice data (choiceType, text, clearImage, etc.)
 * @param {File|null} image - Optional new choice image (only for Normal type)
 * @param {Object} options - Request options
 */
export const updateChoice = async (choiceId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.uploadPut(`/surveys/choices/${choiceId}`, formData, options);
};

export const deleteChoice = async (choiceId, options = {}) => {
  return api.delete(`/surveys/choices/${choiceId}`, options);
};

export const reorderChoices = async (questionId, choiceIds, options = {}) => {
  return api.post(`/surveys/questions/${questionId}/choices/reorder`, { choiceIds }, options);
};

// ===================
// Public Participation
// ===================

export const getPublicSurvey = async (id, options = {}) => {
  return api.get(`/surveys/public/${id}`, {}, options);
};

export const startParticipation = async (surveyId, data, options = {}) => {
  return api.post(`/surveys/public/${surveyId}/start`, {
    anonymousVisitorKey: data?.anonymousVisitorKey || getAnonymousVisitorKey(),
    ...data,
  }, options);
};

export const submitSurveyResponse = async (surveyId, participantId, answers, options = {}) => {
  return api.post(`/surveys/public/${surveyId}/submit`, { participantId, answers }, options);
};

// ===================
// Statistics & Responses
// ===================

export const getSurveyStatistics = async (id, options = {}) => {
  return api.get(`/surveys/${id}/statistics`, {}, options);
};

export const getParticipants = async (surveyId, params = {}, options = {}) => {
  return api.get(`/surveys/${surveyId}/participants`, params, options);
};

export const getParticipantDetail = async (surveyId, participantId, options = {}) => {
  return api.get(`/surveys/${surveyId}/participants/${participantId}`, {}, options);
};

export const deleteParticipant = async (surveyId, participantId, options = {}) => {
  return api.delete(`/surveys/${surveyId}/participants/${participantId}`, options);
};

export const deleteParticipantsBulk = async (surveyId, participantIds, options = {}) => {
  return api.post(`/surveys/${surveyId}/participants/delete-bulk`, { participantIds }, options);
};

export const exportSurveyResponses = async (surveyId) => {
  const { blob, filename } = await api.download(`/surveys/${surveyId}/export`);
  return { blob, filename };
};

// Helper to download the export file
export const downloadSurveyExport = async (surveyId, surveyName) => {
  try {
    const { blob, filename } = await exportSurveyResponses(surveyId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `survey-${surveyId}-${surveyName.replace(/\s+/g, '-')}-responses.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error('Error downloading survey export:', error);
    return { success: false, error };
  }
};

export default {
  // Enums
  ChoiceType,
  ChoiceTypeLabels,
  // Survey CRUD
  getSurveys,
  getAllSurveys,
  getSurvey,
  createSurvey,
  updateSurvey,
  deleteSurvey,
  publishSurvey,
  unpublishSurvey,
  // Question CRUD
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  // Choice CRUD
  addChoice,
  updateChoice,
  deleteChoice,
  reorderChoices,
  // Participation
  getPublicSurvey,
  startParticipation,
  submitSurveyResponse,
  // Statistics
  getSurveyStatistics,
  getParticipants,
  getSurveyParticipants: getParticipants, // alias for backward compatibility
  getParticipantDetail,
  deleteParticipant,
  deleteParticipantsBulk,
  exportSurveyResponses,
  downloadSurveyExport,
};
